import { Star } from 'lucide-react';
import Reveal from '@/components/Reveal';
import { GOOGLE_REVIEWS, GOOGLE_REVIEWS_URL, GOOGLE_RATING } from '@/constants/reviews';

const GoogleG = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden>
    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.094 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
  </svg>
);

const RatingStars = ({ value = 5, size = 14 }) => (
  <div className="flex items-center gap-0.5" aria-label={`${value} out of 5 stars`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        size={size}
        className={i < value ? 'fill-[#F4B400] text-[#F4B400]' : 'text-[hsl(var(--bb-text-muted))]'}
        strokeWidth={1.5}
      />
    ))}
  </div>
);

/**
 * GoogleReviews — editorial Google reviews block.
 * Props:
 *  - variant: 'home' (3 reviews) | 'page' (all reviews)
 *  - showHeader: bool
 */
export default function GoogleReviews({ variant = 'home', showHeader = true }) {
  const reviews = variant === 'home' ? GOOGLE_REVIEWS.slice(0, 3) : GOOGLE_REVIEWS;

  return (
    <section
      data-testid="google-reviews"
      className="bb-container py-20 md:py-28"
    >
      {showHeader && (
        <div className="grid grid-cols-12 gap-10 items-end mb-12 md:mb-16">
          <div className="col-span-12 lg:col-span-7">
            <Reveal>
              <div className="bb-eyebrow">Loved on Google</div>
            </Reveal>
            <Reveal delay={120}>
              <h2 className="bb-display text-4xl md:text-5xl mt-3 text-[hsl(var(--bb-text))]">
                What our clients
                <span className="bb-italic text-[hsl(var(--bb-gold))]"> say.</span>
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <Reveal delay={180}>
              <div className="flex items-center gap-5">
                <GoogleG size={32} />
                <div>
                  <div className="flex items-center gap-3">
                    <span className="bb-display text-3xl text-[hsl(var(--bb-text))]">{GOOGLE_RATING.score.toFixed(1)}</span>
                    <RatingStars value={5} size={16} />
                  </div>
                  <div className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--bb-text-muted))] mt-1">
                    {GOOGLE_RATING.total
                      ? `${GOOGLE_RATING.total}+ Google reviews`
                      : 'Verified on Google'}
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
        {reviews.map((r, i) => (
          <Reveal
            key={i}
            delay={i * 100}
            data-testid={`google-review-${i}`}
            className="border border-[hsl(var(--bb-text))]/12 p-7 md:p-8 bg-[hsl(var(--bb-ivory-soft))] flex flex-col"
          >
            <div className="flex items-center justify-between">
              <RatingStars value={r.rating} />
              <GoogleG size={18} />
            </div>
            <p className="mt-5 text-[hsl(var(--bb-text))]/85 leading-relaxed text-[15px]">“{r.text}”</p>
            <div className="mt-auto pt-6 border-t border-[hsl(var(--bb-text))]/8 flex items-baseline justify-between">
              <div className="flex items-baseline gap-2">
                <span className="bb-display text-xl text-[hsl(var(--bb-text))]">{r.name}</span>
                {r.isLocalGuide && (
                  <span className="text-[9px] uppercase tracking-[0.22em] text-[hsl(var(--bb-gold))] border border-[hsl(var(--bb-gold))]/40 px-1.5 py-0.5">
                    Local Guide
                  </span>
                )}
              </div>
              <span className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--bb-text-muted))]">
                {r.when}
              </span>
            </div>
          </Reveal>
        ))}
      </div>

      <div className="mt-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <p className="text-sm text-[hsl(var(--bb-text-muted))] max-w-xl">
          We are proud of the relationships we build at the atelier — read every review on our verified Google Business listing.
        </p>
        <a
          href={GOOGLE_REVIEWS_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="google-reviews-cta"
          className="inline-flex items-center gap-3 px-6 py-3.5 bg-[hsl(var(--bb-gold))] text-[hsl(var(--bb-charcoal))] uppercase tracking-[0.22em] text-[11px] font-semibold hover:bg-[hsl(var(--bb-text))] transition-colors duration-500"
        >
          <GoogleG size={16} />
          See all reviews on Google
        </a>
      </div>
    </section>
  );
}
